import { produce } from 'immer';
import { last, prepend, tail } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { createContext, Reducer, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { CustomModalLayout, MessageModalLayout } from '@wix/design-system';
import { Modal } from '../../../components/common';
import { ModalName } from '../../../constants/ModalName';
import { callOrUse } from '../../callOrUse';
import { createHOCWithHookInside } from '../../hoc/createHOCWithHookInside';
import {
  ActionType,
  IModalOptions,
  IModalOptionsAction,
  IModalOptionsContext,
  TModalOptionsState,
} from './useModal.types';

const ModalOptionsContext = createContext<IModalOptionsContext>({
  state: {},
  openedModal: null,
  openModal: (_modalName) => {},
  closeModal: (_modalName) => {},
  setCloseParams: (_modalName) => {},
});

const reducer = produce((draftState: TModalOptionsState, action) => {
  const {
    type,
    payload: { modalName, isOpen, additionalOptions, closeParams },
  } = action;

  switch (type) {
    case ActionType.SetOpen: {
      if (!draftState[modalName]) {
        draftState[modalName] = {};
      }

      draftState[modalName].isOpen = isOpen;
      draftState[modalName].additionalOptions = additionalOptions;
      draftState[modalName].closeParams = closeParams;
      return;
    }

    case ActionType.SetCloseParams: {
      if (!draftState[modalName]) {
        draftState[modalName] = {};
      }

      draftState[modalName].closeParams = { ...draftState[modalName].closeParams, ...closeParams };
      return;
    }

    default:
      return;
  }
});

export const ModalOptionsProvider = ({ children, initialModalOptions = {} }) => {
  const [state, dispatch] = useReducer<Reducer<TModalOptionsState, IModalOptionsAction>>(reducer, initialModalOptions);
  const [openedModals, setOpenedModals] = useState([]);

  const openModal = useCallback(
    (modalName: ModalName, additionalOptions = {}) => {
      setOpenedModals(prepend(modalName, openedModals));

      dispatch({ type: ActionType.SetOpen, payload: { modalName, isOpen: true, additionalOptions } });
    },
    [openedModals],
  );

  const closeModal = useCallback(
    (modalName: ModalName) => {
      setOpenedModals(tail(openedModals));

      dispatch({
        type: ActionType.SetOpen,
        payload: { modalName, isOpen: false, additionalOptions: {}, closeParams: {} },
      });
    },
    [openedModals],
  );

  const setCloseParams = useCallback((modalName: ModalName, closeParams = {}) => {
    dispatch({ type: ActionType.SetCloseParams, payload: { modalName, closeParams } });
  }, []);

  return (
    <ModalOptionsContext.Provider
      value={{ state, openedModal: last(openedModals), openModal, closeModal, setCloseParams }}
    >
      {children}
    </ModalOptionsContext.Provider>
  );
};

const empty = {};

export const useModalHandlersAndOptions = (modalName: ModalName) => {
  const { state, openModal, closeModal, setCloseParams } = useContext(ModalOptionsContext);

  const { isOpen = false, additionalOptions = empty, closeParams = {} } = state[modalName] || {};

  return {
    isOpen,
    additionalOptions,
    closeParams,
    openModal: useCallback(
      (additionalOptions = {}) => openModal(modalName, additionalOptions),
      [modalName, additionalOptions],
    ),
    closeModal: useCallback(() => closeModal(modalName), [modalName]),
    setCloseParams: useCallback(
      (closeParams = {}) => setCloseParams(modalName, closeParams),
      [modalName, setCloseParams],
    ),
  };
};

export const useModal = (modalName: ModalName, modalOptions: IModalOptions) => {
  const {
    shouldCloseOnOverlayClick,
    title,
    subtitle,
    content,
    cancelText,
    confirmText,
    disableCancel,
    disableConfirmation,
    noBodyPadding,
    width,
    height,
    maxHeight,
    overflowY,
    hideFooter,
    sideActions,
    onOpen = noop,
    onClose = noop,
    onCancel = noop,
    onOk = noop,
    skin,
    image,
    onAfterClose = noop,
    onCloseWithCloseButtonClick = noop,
    onCloseWithOverlayClick = noop,
    onHelpButtonClick,
    customLayout = false,
    primaryButtonProps = undefined,
    secondaryButtonProps = undefined,
    closeOnSubmit = true,
    showHeaderDivider = true,
    showFooterDivider = false,
    shouldDisplayCloseButton = true,
  } = modalOptions;

  const { isOpen, closeModal, additionalOptions, closeParams, setCloseParams } = useModalHandlersAndOptions(modalName);

  const handleOnAfterOpen = useCallback(() => {
    onOpen(additionalOptions);
  }, [onOpen, additionalOptions]);

  const handleModalCancel = useCallback(() => {
    if (disableCancel) {
      return false;
    }
    onCancel({ additionalOptions });
    closeModal();
  }, [onCancel, closeModal, disableCancel, additionalOptions]);

  const handleModalOK = useCallback(() => {
    if (disableConfirmation) {
      return false;
    }

    onOk({ additionalOptions, closeParams });

    if (closeOnSubmit) {
      closeModal();
    }
  }, [disableConfirmation, onOk, additionalOptions, closeParams, closeOnSubmit, closeModal]);

  const handleOnAfterClose = useCallback(() => {
    onAfterClose(additionalOptions);
  }, [additionalOptions, onAfterClose]);

  const handleModalClose = useCallback(() => {
    onClose({ additionalOptions, closeParams });
    closeModal();
  }, [onClose, closeParams, closeModal, additionalOptions]);

  const handleCloseWithCloseButton = useCallback(() => {
    onCloseWithCloseButtonClick(closeParams);
    handleModalClose();
  }, [handleModalClose, onCloseWithCloseButtonClick, closeParams]);

  const handleCloseWithOverlay = useCallback(() => {
    // `handleCloseWithOverlay` callback can be triggered multiple times by rapidly clicking on overlay.
    // To avoid tracking close event more then once we check if modal already closed.
    if (!isOpen) {
      return;
    }

    onCloseWithOverlayClick(closeParams);
    handleModalClose();
  }, [isOpen, handleModalClose, onCloseWithOverlayClick, closeParams]);

  const modalContent = useMemo(
    () =>
      callOrUse(content, {
        closeModal: handleModalClose,
        additionalOptions,
        setModalCloseParams: setCloseParams,
      }),
    [content, handleModalClose, additionalOptions, setCloseParams],
  );

  const modalTitle = useMemo(
    () =>
      callOrUse(title, {
        additionalOptions,
      }),
    [title, additionalOptions],
  );

  const modalConfirmText = useMemo(
    () =>
      callOrUse(confirmText, {
        additionalOptions,
      }),
    [confirmText, additionalOptions],
  );

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      onRequestClose={handleCloseWithOverlay}
      contentLabel={modalName}
      onAfterOpen={handleOnAfterOpen}
      onAfterClose={handleOnAfterClose}
    >
      {customLayout ? (
        <CustomModalLayout
          content={modalContent}
          dataHook={modalName}
          title={modalTitle}
          subtitle={subtitle}
          skin={skin}
          height={height}
          maxHeight={maxHeight}
          overflowY={overflowY}
          width={width}
          primaryButtonText={hideFooter ? null : modalConfirmText}
          primaryButtonOnClick={hideFooter ? null : handleModalOK}
          primaryButtonProps={
            disableConfirmation
              ? {
                  ...primaryButtonProps,
                  disabled: true,
                }
              : primaryButtonProps
          }
          secondaryButtonText={hideFooter ? null : (cancelText as string)}
          secondaryButtonOnClick={hideFooter || !cancelText ? null : handleModalCancel}
          secondaryButtonProps={secondaryButtonProps}
          removeContentPadding={noBodyPadding}
          sideActions={sideActions}
          helpButtonProps={{ onClick: onHelpButtonClick }}
          closeButtonProps={{ onClick: shouldDisplayCloseButton ? handleCloseWithCloseButton : undefined }}
          showHeaderDivider={showHeaderDivider}
          showFooterDivider={showFooterDivider}
        />
      ) : (
        <MessageModalLayout
          dataHook={modalName}
          title={modalTitle}
          subtitle={subtitle}
          skin={skin}
          primaryButtonOnClick={handleModalOK}
          primaryButtonProps={
            disableConfirmation
              ? {
                  ...primaryButtonProps,
                  disabled: true,
                }
              : primaryButtonProps
          }
          primaryButtonText={modalConfirmText}
          secondaryButtonOnClick={cancelText ? handleModalCancel : undefined}
          secondaryButtonProps={
            disableCancel
              ? {
                  ...secondaryButtonProps,
                  disabled: true,
                }
              : secondaryButtonProps
          }
          secondaryButtonText={cancelText as string}
          onCloseButtonClick={shouldDisplayCloseButton ? handleCloseWithCloseButton : undefined}
          onHelpButtonClick={onHelpButtonClick}
          sideActions={sideActions}
          illustration={image}
        >
          {modalContent}
        </MessageModalLayout>
      )}
    </Modal>
  );
};

export const useOpenedModal = () => {
  const { openedModal } = useContext(ModalOptionsContext);

  return { openedModal };
};

/**
 * @deprecated use "useOpenedModal"
 */
export const withOpenedModalName = createHOCWithHookInside('withOpenedModalName', useOpenedModal);
